import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "@/components/Spinner/Spinner";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { useGetHubspotSyncLogsQuery } from "@/pages/Private/redux/hubspot/hubspot.api";

import { HubspotEntityType } from "../schema/hubspot";
import { firstSyncLogError, syncLogPageRange } from "./hubspotSyncLog.utils";

const LIMIT = 20;

const formatDate = (dateStr: string): string =>
	new Date(dateStr).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

const statusColors: Record<string, string> = {
	SUCCESS: "bg-green-100 text-green-800",
	PARTIAL: "bg-yellow-100 text-yellow-800",
	FAILED: "bg-red-100 text-red-800",
	PENDING: "bg-gray-100 text-gray-800",
	IN_PROGRESS: "bg-blue-100 text-blue-800",
};

/**
 * HubSpot → Sync Logs (PREC-4301). The other four CRMs had this tab; HubSpot, the most used one,
 * did not, so a failed sync showed only as a red dot on one lead's CRM icon.
 */
export const HubspotSyncLogsTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`company.hubspot.syncLogs.${key}`, options);

	const [entityFilter, setEntityFilter] = useState<string | undefined>(undefined);
	const [offset, setOffset] = useState(0);

	const { data, isLoading } = useGetHubspotSyncLogsQuery({
		entityType: entityFilter,
		limit: LIMIT,
		offset,
	});

	const entityOptions: DropdownAutoCompleteItem[] = useMemo(
		() => [
			{ id: "ALL", title: t("company.hubspot.syncLogs.allEntities") },
			{ id: HubspotEntityType.CONTACT, title: "Contact" },
			{ id: HubspotEntityType.COMPANY, title: "Company" },
			{ id: HubspotEntityType.LEAD, title: "Lead" },
			{ id: HubspotEntityType.DEAL, title: "Deal" },
		],
		[t]
	);

	if (isLoading) {
		return (
			<div className="flex justify-center py-12">
				<Spinner />
			</div>
		);
	}

	const items = data?.logs ?? [];
	const total = data?.total ?? 0;
	const range = syncLogPageRange(offset, items.length, total);

	return (
		<div className="flex flex-col w-full gap-4">
			<div className="text-sm text-text-meta">{ts("description")}</div>

			<div className="w-full max-w-[250px]">
				<Dropdown
					data={entityOptions}
					defaultValue={entityOptions[0]}
					floating={true}
					handleSelect={(value) => {
						setEntityFilter(value.id === "ALL" ? undefined : (value.id as string));
						setOffset(0);
					}}
					label={ts("filterByEntity")}
				/>
			</div>

			{items.length === 0 ? (
				<div className="text-center py-12 text-text-meta text-sm">{ts("noLogs")}</div>
			) : (
				<div className="rounded-xl border border-border-light bg-brand-white overflow-hidden">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border-light bg-gray-50">
								{[
									"entityType",
									"status",
									"synced",
									"failed",
									"error",
									"startedAt",
									"completedAt",
								].map((column) => (
									<th key={column} className="px-4 py-3 text-left font-medium text-text-meta">
										{ts(column)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{items.map((log) => {
								const error = firstSyncLogError(log);

								return (
									<tr key={log.id} className="border-b border-border-light last:border-b-0">
										<td className="px-4 py-3 text-text">{log.entityType}</td>
										<td className="px-4 py-3">
											<span
												className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[log.status] ?? "bg-gray-100 text-gray-800"}`}
											>
												{log.status}
											</span>
										</td>
										<td className="px-4 py-3 text-text">{log.recordsSynced}</td>
										<td className="px-4 py-3 text-text">{log.recordsFailed}</td>
										<td
											className="px-4 py-3 text-text-meta text-xs max-w-[260px] truncate"
											title={error ?? undefined}
										>
											{error ?? "—"}
										</td>
										<td className="px-4 py-3 text-text-meta">
											{formatDate(log.startedAt ?? log.createdAt)}
										</td>
										<td className="px-4 py-3 text-text-meta">
											{log.completedAt ? formatDate(log.completedAt) : "—"}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{range && total > LIMIT && (
				<div className="flex justify-center items-center gap-3 mt-2 text-sm">
					<button
						className="px-3 py-1 rounded border border-border-light disabled:opacity-50"
						disabled={offset === 0}
						type="button"
						onClick={() => setOffset((prev) => Math.max(0, prev - LIMIT))}
					>
						{t("basics.previous")}
					</button>
					<span className="text-text-meta">{ts("pageOf", range)}</span>
					<button
						className="px-3 py-1 rounded border border-border-light disabled:opacity-50"
						disabled={range.to >= total}
						type="button"
						onClick={() => setOffset((prev) => prev + LIMIT)}
					>
						{t("basics.next")}
					</button>
				</div>
			)}
		</div>
	);
};
